import React from 'react'

import i18next from '@disco/i18n/i18next'

import { Flex, Progress, Text } from 'components/ds'

import type { Price } from './price'
import { formatPrice } from './price'
import SubscriptionRow from './subscription-row'
import type { Usage } from './utils'

type Props = {
  planName: string
  /** In the plan's billing period */
  price: Price | null
  seats: Usage
  tracks: Usage
}

const isAtLimit = ({ used, allowance }: Usage): boolean => used >= allowance

/** The plan, its price, and how many of its seats and tracks are used. */
const PlanRow = ({
  planName,
  price,
  seats,
  tracks,
}: Props): React.ReactNode => {
  // TODO: STELLA-31 - translate this row's copy with the rest of the section
  const locale = i18next.language
  const formatCount = (count: number): string => count.toLocaleString(locale)
  const tracksUnlimited = tracks.allowance === Infinity
  // Multiplied before dividing so whole percentages stay exact (29/100*100 is 28.999…)
  const tracksPercent =
    tracks.allowance > 0 ? (tracks.used * 100) / tracks.allowance : 100
  // Rounded down so nearly full doesn't read as 100%
  const tracksPercentLabel = new Intl.NumberFormat(locale, {
    style: 'percent',
  }).format(Math.floor(tracksPercent) / 100)
  const tracksUsage = tracksUnlimited
    ? 'Unlimited'
    : `${formatCount(tracks.used)} / ${formatCount(
        tracks.allowance,
      )} (${tracksPercentLabel})`

  return (
    <SubscriptionRow
      title={`${planName} plan`}
      subtitle={
        seats.allowance === Infinity
          ? 'Unlimited seats'
          : `${formatCount(seats.used)} / ${formatCount(seats.allowance)} ${
              seats.allowance === 1 ? 'seat' : 'seats'
            } used`
      }
      subtitleColor={isAtLimit(seats) ? 'red' : 'gray'}
      aside={
        price && (
          <Text size="1" weight="medium" color="gray">
            {formatPrice(price)}
          </Text>
        )
      }
    >
      <Flex direction="column" gap="2">
        <Flex justify="between" gap="4">
          <Text size="1" color="gray">
            Track count
          </Text>
          <Text size="1" color={isAtLimit(tracks) ? 'red' : 'gray'}>
            {tracksUsage}
          </Text>
        </Flex>
        <Progress
          size="2"
          // Over quota stops at full, like at quota; unlimited shows full too
          value={tracksUnlimited ? 100 : Math.min(tracksPercent, 100)}
          aria-label="Track count"
          // Otherwise unlimited is announced as 100%, the same as at quota
          getValueLabel={() => tracksUsage}
        />
      </Flex>
    </SubscriptionRow>
  )
}

export default PlanRow
